import { useState, MouseEvent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import { Menu, Button, DropdownLink, DropdownText } from './Dropdown.style';
import { openMenuId } from 'menuState';
import { useSignals } from '@preact/signals-react/runtime';

export type DropdownItem = {
  name: string;
  to: string;
};

type HeaderDropdownProps = {
  isScrolled: boolean;
  label: string;
  items: DropdownItem[];
  color: string;
  isActive?: boolean;
  activeColor?: string;
  isFilter?: boolean;
  onSelect?: (value: string) => void;
  anchorVerticalOrigin?: number | 'center' | 'bottom' | 'top';
  anchorHorizontalOrigin?: number | 'center' | 'left' | 'right';
};

export const Dropdown = ({
  label,
  items,
  isScrolled,
  color,
  isActive,
  activeColor,
  onSelect,
  anchorVerticalOrigin = 'bottom',
  anchorHorizontalOrigin = 'left',
}: HeaderDropdownProps) => {
  useSignals();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
    openMenuId.value = label;
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
    openMenuId.value = null;
  };

  useEffect(() => {
    if (isOpen) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <Box mr={1}>
      <Button
        sx={{
          color,
          fontSize: '16px',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          padding: '8px',
          background: isActive && activeColor ? `${activeColor}!important` : 'transparent',
        }}
        isScrolled={isScrolled}
        aria-haspopup="menu"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls="dropdown-menu"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {label}
      </Button>

      <Menu
        open={isOpen}
        sx={{
          zIndex: 9999,
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: anchorVerticalOrigin,
          horizontal: anchorHorizontalOrigin,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
        disableScrollLock={!onSelect}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box px="18px" py="10px">
            {items.map(({ name, to }, index) => {
              return (
                <Box key={index}>
                  {onSelect ? (
                    <DropdownText
                      onClick={() => {
                        onSelect(name);
                        handleClose();
                      }}
                    >
                      {name}
                    </DropdownText>
                  ) : (
                    <DropdownLink to={to} onClick={handleClose}>
                      {name}
                    </DropdownLink>
                  )}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Menu>
    </Box>
  );
};
